import React, {useEffect} from "react";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {Button} from "biggerui";

import {useAuth} from "hooks/useAuth";
import {useMyScenes} from "hooks/useMyScenes";
import {formatSceneContext} from "./sceneLabel";

import "../../App.css";
import "../../css/fonts.css";
import "biggerui/styles.css";
import "./index.css";

// /scenes sits outside the editor's AppRoot, so this route provides its own
// QueryClient. Every mount in the process shares the module-level cache, which
// suits one browser page and obliges a test to clear the cache between cases.
export const queryClient = new QueryClient();

// A visual harness polls window.__renderReady, so each caller sets the flag only
// after its own terminal state renders. The editor and admin shells expose the
// same signal.
function markRenderReady() {
  (window as Window & {__renderReady?: boolean}).__renderReady = true;
  document.body.classList.add("image-ready");
}

// toLocaleString renders in the host locale and timezone, so a snapshot harness
// must pin both or mask the cell. An unparseable timestamp passes through
// unchanged, which keeps a bad value visible rather than blank.
function formatSavedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const SceneList = () => {
  const {
    data,
    isPending,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useMyScenes();

  // The list settles on success or on error, so readiness fires at that point and
  // a harness never captures the "Loading…" frame.
  useEffect(() => {
    if (!isPending) markRenderReady();
  }, [isPending]);

  if (isPending) {
    return <div className="my-scenes-status">Loading your scenes…</div>;
  }

  const scenes = data?.pages.flatMap((p) => p.data) ?? [];

  // A first-load failure replaces the page with an error, whereas a failed
  // "load more" keeps the rows already on screen.
  if (scenes.length === 0) {
    if (isError) {
      const detail = error instanceof Error ? `: ${error.message}` : "";
      return (
        <div className="my-scenes-status my-scenes-error">
          <p>Couldn’t load your scenes{detail}.</p>
          <Button color="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div className="my-scenes-empty">
        <p className="my-scenes-empty-title">No saved scenes yet</p>
        <p className="my-scenes-empty-text">
          Scenes you save show up here, newest first. Open the editor and hit Save.
        </p>
      </div>
    );
  }

  return (
    <>
      <ul className="my-scenes-list">
        {scenes.map((s) => {
          // The row prefers the headline, then the category label, then the scene
          // id, because an older scene carries neither of the first two.
          const label = s.title || formatSceneContext(s.context) || s.sceneId;
          return (
            <li key={s.sceneId} className="my-scenes-item">
              <Link to={`/scene/${s.sceneId}`} className="my-scenes-item-link">
                <span className="my-scenes-item-label">{label}</span>
                <span className="my-scenes-item-meta">
                  {formatSavedAt(s.createdAt)}
                  <span className="my-scenes-item-id"> · {s.sceneId}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {(hasNextPage || isFetchNextPageError) && (
        <div className="my-scenes-more">
          <Button color="secondary" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading…" : isFetchNextPageError ? "Retry" : "Load more"}
          </Button>
          {isFetchNextPageError && (
            <span className="my-scenes-error">Couldn’t load more scenes.</span>
          )}
        </div>
      )}
    </>
  );
};

const MyScenesContent = () => {
  const {user, isLoading} = useAuth();

  // The signed-out state renders no list, so readiness fires here, whereas the
  // signed-in path leaves the signal to SceneList. A pending auth check keeps
  // the page not-ready.
  useEffect(() => {
    if (!isLoading && !user) markRenderReady();
  }, [isLoading, user]);

  if (isLoading) {
    return (
      <div className="my-scenes-shell">
        <div className="my-scenes-content">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-scenes-shell">
        <div className="my-scenes-access">
          <h2>Sign in to see your scenes</h2>
          <p>Your saved scenes are tied to your account.</p>
          <a href="/auth/login?next=%2Fscenes" className="my-scenes-back-link">
            Log in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="my-scenes-shell">
      <nav className="my-scenes-nav">
        <a href="/" className="my-scenes-back-link">
          Back to editor
        </a>
        <span className="my-scenes-user">{user.email}</span>
      </nav>
      <div className="my-scenes-content">
        <div className="my-scenes-header">
          <h1 className="my-scenes-title">My scenes</h1>
          <p className="my-scenes-subtitle">Everything you’ve saved, newest first.</p>
        </div>
        <SceneList />
      </div>
    </div>
  );
};

const MyScenes = () => (
  <QueryClientProvider client={queryClient}>
    <MyScenesContent />
  </QueryClientProvider>
);

export default MyScenes;
